import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { MaterialModule } from '../../../../material.module';
import { TablerIconsModule } from 'angular-tabler-icons';
import { DealsService } from '../../../../core/services/deals.service';
import { AuthService } from '../../../../core/services/auth.service';
import { HelperService } from '../../../../core/services/helper.service';
import { DomSanitizer } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import {
  FileDropzoneComponent,
  FileDropzonePreviewDirective,
} from 'src/app/core/components/file-dropzone/file-dropzone.component';

@Component({
  selector: 'app-account-setting',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MaterialModule,
    ReactiveFormsModule,
    FormsModule,
    TablerIconsModule,
    FileDropzoneComponent,
    FileDropzonePreviewDirective
  ],
  templateUrl: './account-setting.component.html',
  styleUrl: './account-setting.component.scss'
})
export class AccountSettingComponent implements OnInit {
  selectedTabIndex = 0;
  isSuperAdmin = false;

  // Formulaires
  accountForm!: FormGroup;
  passwordForm!: FormGroup;
  personalDetailsForm!: FormGroup;
  notificationsForm!: FormGroup;
  billingForm!: FormGroup;
  configForm!: FormGroup;

  // Données du profil
  profileImage = 'assets/images/profile/user-1.jpg';
  userProfile: any = null;

  // Options pour le nombre d'éléments par page
  itemsPerPageOptions = [10, 20, 50, 100, 200];
  defaultItemsPerPage = 10;

  // Liste de toutes les pages avec leurs configurations
  // Si requiredRoles est vide [], la page est accessible à tous
  // Si requiredRoles contient des rôles, seuls les utilisateurs avec au moins un de ces rôles peuvent la configurer
  paginationPages: { pageId: string; title: string; description: string; icon: string; requiredRoles: string[]; pageSizeOptions?: number[] }[] = [
    {
      pageId: 'all-contact',
      title: 'All Contacts',
      description: 'List of all contacts in the application',
      icon: 'contacts',
      requiredRoles: [] // Accessible à tous
    },
    {
      pageId: 'all-company',
      title: 'All Companies',
      description: 'List of all companies',
      icon: 'business',
      requiredRoles: [] // Accessible à tous
    },
    {
      pageId: 'all-lenders',
      title: 'All Lenders',
      description: 'List of all lenders',
      icon: 'account_balance',
      requiredRoles: [] // Accessible à tous
    },
    {
      pageId: 'users',
      title: 'Users',
      description: 'System user management',
      icon: 'people',
      requiredRoles: ['admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'all-live-deals',
      title: 'Live Deals',
      description: 'List of all live deals',
      icon: 'trending_up',
      requiredRoles: ['exec', 'm_exec', 'admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'closed-deals',
      title: 'Closed Deals',
      description: 'List of all closed deals',
      icon: 'check_circle',
      requiredRoles: ['exec', 'm_exec', 'admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'deal-synopsis-payment-calendar-cash',
      title: 'Payment Calendar — Cash',
      description: 'Cash table in deal synopsis payment calendar (individual view)',
      icon: 'payments',
      requiredRoles: ['exec', 'm_exec', 'admin', 'superadmin', 'mega_admin'],
      pageSizeOptions: [5, 10, 20],
    },
    {
      pageId: 'deal-synopsis-payment-calendar-pik',
      title: 'Payment Calendar — PIK',
      description: 'PIK table in deal synopsis payment calendar (individual view)',
      icon: 'percent',
      requiredRoles: ['exec', 'm_exec', 'admin', 'superadmin', 'mega_admin'],
      pageSizeOptions: [5, 10, 20],
    },
    {
      pageId: 'deal-synopsis-payment-calendar-commitment',
      title: 'Payment Calendar — Commitment fee',
      description: 'Commitment fee table in deal synopsis payment calendar (individual view)',
      icon: 'request_quote',
      requiredRoles: ['exec', 'm_exec', 'admin', 'superadmin', 'mega_admin'],
      pageSizeOptions: [5, 10, 20],
    },
    {
      pageId: 'kyc-company',
      title: 'KYC Companies',
      description: 'KYC management for companies',
      icon: 'verified_user',
      requiredRoles: ['kyc_bo', 'ext_kyc_bo', 'kyc_validator', 'admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'cde-management',
      title: 'CDE Management',
      description: 'Management of engagement decision committees',
      icon: 'gavel',
      requiredRoles: ['cde_admin', 'cde_manager', 'admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'cde-management-history',
      title: 'CDE History',
      description: 'History of CDE decisions',
      icon: 'history',
      requiredRoles: ['cde_admin', 'cde_manager', 'admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'crm-dashboard-appointments',
      title: 'CRM Appointments',
      description: 'List of appointments in the CRM dashboard',
      icon: 'event',
      requiredRoles: ['crm_manager', 'busdev', 'admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'crm-dashboard-opportunities',
      title: 'CRM Opportunities',
      description: 'List of opportunities in the CRM dashboard',
      icon: 'business_center',
      requiredRoles: ['crm_manager', 'busdev', 'admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'crm-all-appointments',
      title: 'All Appointments',
      description: 'Complete list of all CRM appointments',
      icon: 'calendar_today',
      requiredRoles: ['crm_manager', 'busdev', 'admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'crm-live-deals-busdev',
      title: 'Live Deals BusDev',
      description: 'List of live deals for business development',
      icon: 'rocket_launch',
      requiredRoles: ['busdev', 'crm_manager', 'admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'crm-opportunities',
      title: 'Opportunities',
      description: 'CRM opportunity management',
      icon: 'work',
      requiredRoles: ['crm_manager', 'busdev', 'admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'crm-opportunity-lost',
      title: 'Lost Opportunities',
      description: 'List of lost opportunities',
      icon: 'cancel',
      requiredRoles: ['crm_manager', 'busdev', 'admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'kyc-awaiting-assignment',
      title: 'KYC Awaiting Assignment',
      description: 'KYC awaiting assignment',
      icon: 'hourglass_empty',
      requiredRoles: ['kyc_bo', 'ext_kyc_bo', 'kyc_validator', 'admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'kyc-companies',
      title: 'KYC Companies',
      description: 'List of companies with KYC',
      icon: 'corporate_fare',
      requiredRoles: ['kyc_bo', 'ext_kyc_bo', 'kyc_validator', 'admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'kyc-lenders',
      title: 'KYC Lenders',
      description: 'List of lenders with KYC',
      icon: 'account_balance',
      requiredRoles: ['kyc_bo', 'ext_kyc_bo', 'kyc_validator', 'admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'kyc-company-history',
      title: 'KYC Company History',
      description: 'KYC history for a company',
      icon: 'timeline',
      requiredRoles: ['kyc_bo', 'ext_kyc_bo', 'kyc_validator', 'admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'kyc-dashboard-payment',
      title: 'KYC Payments',
      description: 'KYC payments dashboard',
      icon: 'payment',
      requiredRoles: ['kyc_bo', 'ext_kyc_bo', 'kyc_validator', 'admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'kyc-lender-history',
      title: 'KYC Lender History',
      description: 'KYC history for a lender',
      icon: 'account_balance_wallet',
      requiredRoles: ['kyc_bo', 'ext_kyc_bo', 'kyc_validator', 'admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'iban-jm',
      title: 'IBAN JM',
      description: 'IBAN JM management',
      icon: 'account_balance',
      requiredRoles: ['billing', 'billing_manager', 'admin', 'superadmin', 'mega_admin']
    },
    {
      pageId: 'billing-time-management',
      title: 'Time Management',
      description: 'Billing time management dashboard',
      icon: 'schedule',
      requiredRoles: ['superadmin', 'admin', 'billing', 'timemanager']
    },
    {
      pageId: 'upcoming-invoices',
      title: 'Upcoming Invoices',
      description: 'View of all upcoming invoices',
      icon: 'calendar_today',
      requiredRoles: ['superadmin', 'admin', 'billing']
    },
    {
      pageId: 'pennylane-subscriptions-all',
      title: 'Pennylane Subscriptions - All',
      description: 'Pagination for "All" filter in Pennylane subscriptions',
      icon: 'subscriptions',
      requiredRoles: ['superadmin', 'admin', 'billing']
    },
    {
      pageId: 'pennylane-subscriptions-draft',
      title: 'Pennylane Subscriptions - Draft',
      description: 'Pagination for "Draft" filter',
      icon: 'subscriptions',
      requiredRoles: ['superadmin', 'admin', 'billing']
    },
    {
      pageId: 'pennylane-subscriptions-not_started',
      title: 'Pennylane Subscriptions - Not Started',
      description: 'Pagination for "Not Started" filter',
      icon: 'subscriptions',
      requiredRoles: ['superadmin', 'admin', 'billing']
    },
    {
      pageId: 'pennylane-subscriptions-in_progress',
      title: 'Pennylane Subscriptions - In Progress',
      description: 'Pagination for "In Progress" filter',
      icon: 'subscriptions',
      requiredRoles: ['superadmin', 'admin', 'billing']
    },
    {
      pageId: 'pennylane-subscriptions-stopped',
      title: 'Pennylane Subscriptions - Stopped',
      description: 'Pagination for "Stopped" filter',
      icon: 'subscriptions',
      requiredRoles: ['superadmin', 'admin', 'billing']
    },
    {
      pageId: 'pennylane-subscriptions-finished',
      title: 'Pennylane Subscriptions - Finished',
      description: 'Pagination for "Finished" filter',
      icon: 'subscriptions',
      requiredRoles: ['superadmin', 'admin', 'billing']
    },
    {
      pageId: 'pennylane-subscriptions-pending',
      title: 'Pennylane Subscriptions - Pending',
      description: 'Pagination for "Pending" filter',
      icon: 'subscriptions',
      requiredRoles: ['superadmin', 'admin', 'billing']
    },
    {
      pageId: 'billing-time-management',
      title: 'Time Management',
      description: 'Billing time management dashboard',
      icon: 'schedule',
      requiredRoles: ['superadmin', 'admin', 'billing', 'timemanager']
    },
    {
      pageId: 'upcoming-invoices',
      title: 'Upcoming Invoices',
      description: 'View of all upcoming invoices',
      icon: 'calendar_today',
      requiredRoles: ['superadmin', 'admin', 'billing']
    },
    {
      pageId: 'billing-dashboard-deals',
      title: 'Billing Dashboard - Deals',
      description: 'New billing requests for deals',
      icon: 'description',
      requiredRoles: ['superadmin', 'admin', 'billing']
    },
    {
      pageId: 'billing-dashboard-timesheets',
      title: 'Billing Dashboard - Timesheets',
      description: 'New billing requests for timesheets',
      icon: 'schedule',
      requiredRoles: ['superadmin', 'admin', 'billing']
    },
    {
      pageId: 'billing-tracking',
      title: 'Billing Tracking',
      description: 'Billing tracking for the Business Developers',
      icon: 'track_changes',
      requiredRoles: ['superadmin', 'admin', 'billing']
    },
    {
      pageId: 'invoice-recovery-live',
      title: 'Invoice Recovery - Live',
      description: 'Complete view of all invoices with overdue status (Live)',
      icon: 'receipt',
      requiredRoles: ['superadmin', 'admin', 'billing']
    },
    {
      pageId: 'invoice-recovery-history',
      title: 'Invoice Recovery - History',
      description: 'Complete view of all invoices with overdue status (History)',
      icon: 'history',
      requiredRoles: ['superadmin', 'admin', 'billing']
    }
  ];

  // Stockage des préférences de pagination par page
  pagePaginationPreferences: { [key: string]: number } = {};

  // Liste des BU
  listeBu: any[] = [];

  // Options d'emplacement
  locations = [
    { value: 'Londres', label: 'Londres' },
    { value: 'Luxembourg', label: 'Luxembourg' },
    { value: 'Milan', label: 'Milan' },
    { value: 'Madrid', label: 'Madrid' }
  ];

  // Préférences de notifications (initialisé vide, sera rempli depuis le backend)
  notificationPreferences: { [key: string]: boolean } = {};

  // Configuration des notifications avec leurs rôles requis (chargée depuis le backend)
  // Si requiredRoles est vide [], la notification est accessible à tous
  // Si requiredRoles contient des rôles, seuls les utilisateurs avec au moins un de ces rôles peuvent l'activer
  notificationConfig: { key: string; title: string; desc: string; icon: string; requiredRoles: string[] }[] = [];

  // Mapping des icônes pour les notifications (car les icônes ne sont pas dans la base de données)
  private notificationIcons: { [key: string]: string } = {
    'invitation_appointment': 'event',
    'assignment': 'assignment',
    'kyc_assignment_notification': 'notification_important',
    'kyc_assignment': 'verified_user',
    'confirmation_cde_busdev': 'check_circle',
    'invitation_cde': 'mail',
    'new_cde_busdev': 'new_releases',
    'new_cde': 'add_circle',
    'response_cde_busdev': 'reply',
    'review_cde': 'rate_review',
    'relance1': 'schedule',
    'relance2': 'schedule',
    'relance3': 'schedule',
    'leaves_notif': 'event_available',
    'alertdate_hebdo': 'calendar_today',
    'forget_password': 'lock_reset',
    'hebdoactivity': 'bar_chart',
    'mailrelanceagency': 'business',
    'mailsender': 'send',
    'mailsimple': 'mail_outline',
    'new_user': 'person_add',
    'pub_message_atr': 'public',
    'relanceclauseclient': 'description',
    'investors_access': 'account_box',
    'investors_notification_file_update': 'folder_shared',
    'newlaunch': 'rocket_launch',
    'snapshot': 'camera_alt',
    'supportticket': 'support_agent',
    'relance_du_25': 'event_note',
    'relance_du_27': 'event_note',
    'timesheets_invoiced': 'receipt',
    'timesheets_validated': 'check_circle',
    'noticesend': 'article'
  };

  // Rôles de l'utilisateur actuel
  userRoles: string[] = [];

  // Liste de tous les rôles disponibles (pour le menu déroulant)
  allAvailableRoles: { id: number; name: string; description?: string; selected: boolean }[] = [];

  // Liste de tous les rôles disponibles pour le filtre de pagination (copie séparée)
  allAvailablePaginationRoles: { id: number; name: string; description?: string; selected: boolean }[] = [];

  // Rôles sélectionnés pour filtrer les notifications
  selectedFilterRoles: string[] = [];

  // Rôles sélectionnés pour filtrer les pages de pagination
  selectedPaginationFilterRoles: string[] = [];

  // Appareils connectés
  devices = [
    {
      name: 'iPhone 14',
      location: 'London UK',
      lastLogin: 'Oct 23 at 1:15 AM',
      icon: 'phone_iphone'
    },
    {
      name: 'Macbook Air',
      location: 'Gujarat India',
      lastLogin: 'Oct 24 at 3:15 AM',
      icon: 'laptop_mac'
    }
  ];

  // Plan actuel
  currentPlan = {
    name: 'Execusive',
    visits: '750.000 Monthly Visits'
  };

  // Méthode de paiement
  paymentMethod = {
    type: 'Visa',
    number: '*****2102',
    date: 'On 26 December, 2023'
  };

  timezone = '(UTC + 02:00) Athens, Bucharest';
  ignoreBrowserTracking = false;

  constructor(
    private fb: FormBuilder,
    private dealservice: DealsService,
    private authService: AuthService,
    private helperService: HelperService,
    private domSanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private router: Router
  ) {
    this.isSuperAdmin = this.authService.isAdmin() && this.authService.currentUser()?.group?.includes('superadmin');
    this.initFormsSkeleton();
  }

  /**
   * Initializes form group structures immediately so [formGroup] in the template
   * always has a FormGroup instance (avoids NG01052 before async data loads).
   */
  private initFormsSkeleton(): void {
    this.accountForm = this.fb.group({
      name: [''],
      location: [''],
      email: [''],
      address: [''],
      storeName: [''],
      phone: ['']
    });
    this.passwordForm = this.fb.group({
      currentPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });
    this.passwordForm.get('confirmPassword')?.valueChanges.subscribe(() => this.passwordForm.updateValueAndValidity());
    this.passwordForm.get('newPassword')?.valueChanges.subscribe(() => this.passwordForm.updateValueAndValidity());

    this.personalDetailsForm = this.fb.group({
      firstName: [''],
      lastName: [''],
      title: [''],
      email: [''],
      phone: [''],
      userBu: [''],
      location: [''],
      adresse: ['']
    });

    this.notificationsForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });

    this.billingForm = this.fb.group({
      businessName: ['Visitor Analytics', Validators.required],
      businessAddress: ['', Validators.required],
      firstName: ['', Validators.required],
      businessSector: ['Arts, Media & Entertainment', Validators.required],
      country: ['Romania', Validators.required],
      lastName: ['', Validators.required]
    });

    this.configForm = this.fb.group({
      itemsPerPage: [this.defaultItemsPerPage, [Validators.required, Validators.min(10), Validators.max(200)]]
    });
  }

  ngOnInit(): void {
    this.loadAllRoles();
    // Rafraîchir la session pour s'assurer d'avoir les dernières données utilisateur (rôles, etc.)
    // Cela est important si l'utilisateur a changé de rôle récemment
    this.authService.checkSession().subscribe({
      next: () => {
        this.initializeComponent();
      },
      error: (error) => {
        this.initializeComponent();
      }
    });
  }

  private initializeComponent(): void {
    // Charger la photo de profil depuis AuthService si disponible
    this.authService.getProfilePic();

    // Récupérer les rôles de l'utilisateur
    const currentUser = this.authService.currentUser();
    this.userRoles = currentUser?.group || [];

    // Recalculer isSuperAdmin dans ngOnInit pour prendre en compte les changements de rôle
    this.isSuperAdmin = this.authService.isAdmin() && (this.userRoles.includes('superadmin') || currentUser?.group?.includes('superadmin'));

    // Initialiser pagePaginationPreferences comme objet vide
    this.pagePaginationPreferences = {};

    this.loadAllRoles();
    this.loadBuList();
    this.loadNotifications();
    // Note: initForms() et loadPaginationPreferences() sont appelés dans loadUserProfile() après le chargement du profil
    this.loadUserProfile();
  }

  /**
   * Charge les notifications accessibles depuis le backend selon les rôles de l'utilisateur
   */
  loadNotifications(): void {
    this.dealservice.get_all_notifications().subscribe({
      next: (response: any) => {
        if (response.status === 'success' && response.data) {
          // Récupérer le nombre total de rôles uniques pour déterminer si une notification est accessible à tous
          const allRoles = new Set<string>();
          response.data.forEach((notif: any) => {
            if (notif.required_roles && Array.isArray(notif.required_roles)) {
              notif.required_roles.forEach((role: string) => allRoles.add(role));
            }
          });
          const totalRolesCount = allRoles.size;

          // Transformer les données de l'API en format attendu par le composant
          this.notificationConfig = response.data.map((notif: any) => {
            const requiredRoles = notif.required_roles || [];
            // Si une notification a tous les rôles (ou presque tous), elle est accessible à tous
            const isAccessibleToAll = requiredRoles.length >= totalRolesCount * 0.9 || requiredRoles.length >= 15;

            return {
              key: notif.key || notif.name,
              title: this.formatNotificationTitle(notif.name),
              desc: notif.description || '',
              icon: this.notificationIcons[notif.key || notif.name] || 'notifications',
              requiredRoles: isAccessibleToAll ? [] : requiredRoles // Si accessible à tous, mettre un tableau vide
            };
          });

          if (this.notificationConfig.length === 0) {
          }

          // Initialiser les préférences par défaut pour toutes les notifications
          this.initializeDefaultPreferences();
        } else {
          this.notificationConfig = [];
        }
      },
      error: (error: any) => {
        this.notificationConfig = [];
      }
    });
  }

  /**
   * Formate le nom de notification en titre lisible
   */
  private formatNotificationTitle(name: string): string {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }


  loadUserProfile(): void {
    this.dealservice.get_current_user_profile().subscribe({
      next: (response: any) => {
        if (response.status === 'success' && response.data) {
          this.userProfile = response.data;
          // Mettre à jour les rôles et isSuperAdmin après avoir chargé le profil
          const currentUser = this.authService.currentUser();
          this.userRoles = currentUser?.group || [];
          this.isSuperAdmin = this.authService.isAdmin() && (this.userRoles.includes('superadmin') || currentUser?.group?.includes('superadmin'));

          // Charger l'image de profil
          if (this.userProfile.image) {
            // Si l'image est déjà une URL complète, l'utiliser directement
            if (this.userProfile.image.startsWith('http://') || this.userProfile.image.startsWith('https://')) {
              this.profileImage = this.userProfile.image;
            } else if (this.userProfile.image.startsWith('/')) {
              // Si c'est un chemin relatif, construire l'URL complète
              this.profileImage = 'https://hr-portal.arturpro.com/api' + this.userProfile.image;
            } else {
              // Sinon, construire l'URL complète
              this.profileImage = 'https://hr-portal.arturpro.com/api/assets/UserImages/' + this.userProfile.image;
            }

            // Vérifier que l'image est accessible
            const testImg = new Image();
            testImg.onload = () => {
              this.cdr.detectChanges();
            };
            testImg.onerror = (err) => {
              // En cas d'erreur, essayer de charger depuis Microsoft
              this.loadMicrosoftPhoto();
            };
            testImg.src = this.profileImage;

            this.cdr.detectChanges();
          } else {
            // Si pas d'image dans la base, récupérer depuis Microsoft Graph API
            this.loadMicrosoftPhoto();
          }

          // Charger les préférences d'email directement depuis user_email_preferences
          this.loadEmailPreferences();

          // Initialiser les formulaires avec les données utilisateur (userProfile est déjà chargé)
          this.initForms();

          // Forcer la détection de changement pour mettre à jour l'interface
          this.cdr.detectChanges();

          // Charger les préférences de pagination pour toutes les pages après le chargement du profil
          this.loadPaginationPreferences();

          // Charger la préférence items_per_page si elle existe
          if (this.userProfile.items_per_page) {
            this.defaultItemsPerPage = this.userProfile.items_per_page;
            // Sauvegarder aussi dans localStorage pour un accès rapide
            localStorage.setItem('user_items_per_page', this.userProfile.items_per_page.toString());
            // Mettre à jour AuthService
            const currentUser: any = this.authService.currentUser();
            if (currentUser) {
              this.authService.updateUserData({ items_per_page: this.userProfile.items_per_page });
            }
            if (this.configForm) {
              this.configForm.patchValue({ itemsPerPage: this.userProfile.items_per_page });
            }
          } else {
            // Si pas de préférence dans le profil, utiliser celle de localStorage ou la valeur par défaut
            const storedValue = localStorage.getItem('user_items_per_page');
            if (storedValue) {
              const parsed = parseInt(storedValue, 10);
              if (!isNaN(parsed) && parsed >= 10 && parsed <= 200) {
                this.defaultItemsPerPage = parsed;
                if (this.configForm) {
                  this.configForm.patchValue({ itemsPerPage: parsed });
                }
              }
            }
          }
        }
      },
      error: (error: any) => {
        // En cas d'erreur, essayer de charger la photo Microsoft
        this.loadMicrosoftPhoto();
      }
    });
  }

  loadMicrosoftPhoto(): void {
    // D'abord, essayer d'utiliser l'image depuis AuthService si elle est déjà chargée
    const authProfilePic = this.authService.profilePic();

    if (authProfilePic) {
      try {
        // Convertir SafeUrl en string
        // SafeUrl a une propriété changingThisBreaksApplicationSecurity qui contient l'URL
        const picUrl = (authProfilePic as any).changingThisBreaksApplicationSecurity ||
          (typeof authProfilePic === 'string' ? authProfilePic : '');

        if (picUrl) {
          this.profileImage = picUrl;
          this.cdr.detectChanges();
          return;
        }
      } catch (e) {
      }
    }

    // Sinon, récupérer la photo depuis Microsoft Graph API
    this.authService.getUserProfilePic().subscribe({
      next: (blob: Blob) => {
        if (blob && blob.size > 0) {
          const urlCreator = window.URL || (window as any).webkitURL;
          const objectUrl = urlCreator.createObjectURL(blob);

          // Mettre à jour l'image
          this.profileImage = objectUrl;
          // Forcer la détection de changement
          this.cdr.detectChanges();

          // Vérifier que l'image se charge correctement
          const testImg = new Image();
          testImg.onload = () => {
          };
          testImg.onerror = (err) => {
            // En cas d'erreur, essayer l'image par défaut
            this.profileImage = 'assets/images/profile/user-1.jpg';
            this.cdr.detectChanges();
          };
          testImg.src = objectUrl;
        } else {
          // Image par défaut si aucune image n'est disponible
          this.profileImage = 'assets/images/profile/user-1.jpg';
          this.cdr.detectChanges();
        }
      },
      error: (error: any) => {
        // Image par défaut en cas d'erreur
        this.profileImage = 'assets/images/profile/user-1.jpg';
        this.cdr.detectChanges();
      }
    });
  }

  loadBuList(): void {
    this.dealservice.get_bu().subscribe({
      next: (data: any) => {
        this.listeBu = data;
        // Forcer la détection de changement après le chargement des BU
        this.cdr.detectChanges();
      },
      error: (error: any) => {
      }
    });
  }

  /**
   * Charge les préférences email de l'utilisateur actuel depuis user_email_preferences
   */
  loadEmailPreferences(): void {
    this.http.get<any>(environment.serverUrl + 'local/get_my_email_preferences').subscribe({
      next: (response: any) => {
        if (response.status === 'success' && response.data) {
          // Les préférences sont déjà dans le format simple {notification_key: enabled}
          this.notificationPreferences = response.data;

          // Initialiser les préférences par défaut pour les notifications qui n'ont pas de préférence
          if (this.notificationConfig.length > 0) {
            this.initializeDefaultPreferences();
          }
        } else {
          this.notificationPreferences = {};
        }
      },
      error: (error: any) => {
        this.notificationPreferences = {};
      }
    });
  }


  onBuChange(buSlug: string): void {
    // Currency field removed, no action needed
  }

  /**
   * Charge les préférences de pagination pour toutes les pages UNIQUEMENT depuis la base de données
   * Pas de localStorage - la base de données est la source de vérité unique
   */
  loadPaginationPreferences(): void {
    // Charger UNIQUEMENT depuis la base de données (userProfile.pagination_preferences)
    let allPreferences: { [key: string]: number } = {};

    if (this.userProfile && this.userProfile.pagination_preferences) {
      // Les préférences viennent directement de la base de données
      allPreferences = this.userProfile.pagination_preferences;
    }

    // Initialiser les préférences pour chaque page
    const legacyPennylaneKey = 'pennylane-subscriptions';
    this.paginationPages.forEach(page => {
      let savedSize = allPreferences[page.pageId];
      // Rétrocompatibilité : pour pennylane-subscriptions-*, utiliser l'ancienne clé globale si la clé spécifique n'existe pas
      if (savedSize == null && page.pageId.startsWith('pennylane-subscriptions-') && allPreferences[legacyPennylaneKey]) {
        savedSize = allPreferences[legacyPennylaneKey];
      }
      const options = this.getPageSizeOptions(page);
      const defaultSize = options[0];
      const parsed = savedSize != null ? parseInt(String(savedSize), 10) : NaN;
      this.pagePaginationPreferences[page.pageId] =
        !isNaN(parsed) && options.includes(parsed) ? parsed : defaultSize;
    });

    this.cdr.detectChanges();
  }

  getPageSizeOptions(page: { pageSizeOptions?: number[] }): number[] {
    return page.pageSizeOptions?.length ? page.pageSizeOptions : this.itemsPerPageOptions;
  }

  /**
   * Retourne toutes les pages de pagination disponibles selon le filtre de rôles
   * Filtre selon :
   * 1. Les rôles de l'utilisateur (seules les pages qu'il peut modifier sont affichées)
   * 2. Le filtre de rôles sélectionné (si des rôles sont sélectionnés)
   */
  getAllPaginationPages() {
    // D'abord, filtrer selon les rôles de l'utilisateur (ne montrer que les pages qu'il peut modifier)
    let filteredPages = this.paginationPages.filter(page =>
      this.canConfigurePage(page.requiredRoles)
    );

    // Si des rôles sont sélectionnés pour le filtre, filtrer les pages
    if (this.selectedPaginationFilterRoles.length > 0) {
      return filteredPages.filter(page => {
        // Si la page n'a pas de rôles requis, elle est accessible à tous (toujours affichée)
        if (!page.requiredRoles || page.requiredRoles.length === 0) {
          return true;
        }
        // Vérifier si au moins un des rôles requis correspond aux rôles sélectionnés
        return page.requiredRoles.some(role => this.selectedPaginationFilterRoles.includes(role));
      });
    }

    return filteredPages;
  }

  /**
   * Retourne les pages accessibles à tous (sans rôles requis)
   */
  getPaginationPagesForAll() {
    return this.paginationPages.filter(page =>
      !page.requiredRoles || page.requiredRoles.length === 0
    );
  }

  /**
   * Retourne les pages avec des rôles requis spécifiques
   * Filtre selon :
   * 1. Les rôles de l'utilisateur (seules les pages qu'il peut modifier sont affichées)
   * 2. Le filtre de rôles sélectionné (si des rôles sont sélectionnés)
   */
  getPaginationPagesWithRoles() {
    // D'abord, filtrer les pages qui ont des rôles requis
    let pagesWithRoles = this.paginationPages.filter(page =>
      page.requiredRoles && page.requiredRoles.length > 0
    );

    // Filtrer selon les rôles de l'utilisateur (ne montrer que les pages qu'il peut modifier)
    pagesWithRoles = pagesWithRoles.filter(page =>
      this.canConfigurePage(page.requiredRoles)
    );

    // Si des rôles sont sélectionnés pour le filtre, filtrer les pages
    if (this.selectedPaginationFilterRoles.length > 0) {
      return pagesWithRoles.filter(page => {
        // Vérifier si au moins un des rôles requis correspond aux rôles sélectionnés
        return page.requiredRoles.some(role => this.selectedPaginationFilterRoles.includes(role));
      });
    }

    return pagesWithRoles;
  }

  /**
   * Vérifie si une page peut être configurée par l'utilisateur
   */
  canConfigurePage(requiredRoles: string[]): boolean {
    if (!requiredRoles || requiredRoles.length === 0) {
      return true; // Accessible à tous
    }

    // Vérifier si l'utilisateur a au moins un des rôles requis
    return requiredRoles.some(role => this.userRoles.includes(role));
  }

  /**
   * Toggle la sélection d'un rôle dans le filtre de pagination
   */
  togglePaginationRoleFilter(role: any): void {
    role.selected = !role.selected;
    this.updateSelectedPaginationFilterRoles();
  }

  /**
   * Met à jour la liste des rôles sélectionnés pour le filtre de pagination
   */
  updateSelectedPaginationFilterRoles(): void {
    this.selectedPaginationFilterRoles = this.allAvailablePaginationRoles
      .filter(role => role.selected)
      .map(role => role.name);
  }

  /**
   * Sélectionne tous les rôles pour le filtre de pagination
   */
  selectAllPaginationRoles(): void {
    this.allAvailablePaginationRoles.forEach(role => role.selected = true);
    this.updateSelectedPaginationFilterRoles();
  }

  /**
   * Désélectionne tous les rôles pour le filtre de pagination
   */
  deselectAllPaginationRoles(): void {
    this.allAvailablePaginationRoles.forEach(role => role.selected = false);
    this.updateSelectedPaginationFilterRoles();
  }

  /**
   * Vérifie si tous les rôles sont sélectionnés pour le filtre de pagination
   */
  get allPaginationRolesSelected(): boolean {
    return this.allAvailablePaginationRoles.length > 0 && this.allAvailablePaginationRoles.every(r => r.selected);
  }

  /**
   * Met à jour la préférence de pagination pour une page spécifique
   * Vérifie d'abord que l'utilisateur a les rôles nécessaires pour modifier cette page
   * Sauvegarde directement en base de données (pas seulement dans localStorage)
   */
  updatePagePagination(pageId: string, pageSize: number): void {
    // Trouver la page correspondante
    const page = this.paginationPages.find(p => p.pageId === pageId);

    // Vérifier les permissions si la page existe
    if (page && !this.canConfigurePage(page.requiredRoles)) {
      console.warn(`Tentative de modification non autorisée de la page ${pageId} par un utilisateur sans les rôles requis`);
      this.helperService.showAlert(
        'Accès refusé',
        'Vous n\'avez pas les rôles nécessaires pour modifier la configuration de cette page.',
        'warning'
      );
      // Recharger les préférences pour restaurer la valeur précédente
      this.loadPaginationPreferences();
      return;
    }

    // Si la page n'existe pas, on laisse quand même la modification (cas d'une nouvelle page)
    // mais on log un avertissement
    if (!page) {
      console.warn(`Page ${pageId} non trouvée dans paginationPages, modification autorisée mais non vérifiée`);
    }

    // Mettre à jour la valeur localement AVANT de sauvegarder (pour l'inclure dans les préférences envoyées)
    this.pagePaginationPreferences[pageId] = pageSize;
    this.cdr.detectChanges();

    // Sauvegarder en base de données (source de vérité unique)
    // Passer toutes les préférences actuelles (incluant la nouvelle valeur) pour éviter d'écraser les autres
    this.authService.savePageSizeForPage(pageId, pageSize, this.pagePaginationPreferences).subscribe({
      next: (response: any) => {
        if (response && response.status === 'success') {
          console.log(`✅ Préférence de pagination sauvegardée en base de données: ${pageId} = ${pageSize}`);

          // Recharger le profil depuis la base pour afficher les vraies données
          this.dealservice.get_current_user_profile().subscribe({
            next: (profileResponse: any) => {
              if (profileResponse.status === 'success' && profileResponse.data) {
                this.userProfile = profileResponse.data;
                // Mettre à jour AuthService avec les nouvelles préférences
                if (this.userProfile.pagination_preferences) {
                  this.authService.updateUserData({ pagination_preferences: this.userProfile.pagination_preferences });
                }
                // Recharger les préférences depuis la base de données
                this.loadPaginationPreferences();
              }
            },
            error: (err: any) => {
              console.error('Erreur lors du rechargement du profil:', err);
              // Mettre à jour quand même l'affichage avec la valeur sauvegardée
              this.pagePaginationPreferences[pageId] = pageSize;
              this.cdr.detectChanges();
            }
          });
        } else {
          console.warn('⚠️ Sauvegarde réussie mais réponse inattendue:', response);
          // Recharger depuis la base quand même
          this.loadPaginationPreferences();
        }
      },
      error: (error: any) => {
        console.error('❌ Erreur lors de la sauvegarde en base de données:', error);
        // Restaurer la valeur depuis la base en cas d'erreur
        this.loadPaginationPreferences();
        this.helperService.showAlert(
          'Erreur de sauvegarde',
          'Impossible de sauvegarder la préférence en base de données. La valeur a été restaurée depuis la base.',
          'error'
        );
      }
    });
  }

  initForms(): void {
    // Forms are already created in initFormsSkeleton(); here we only patch values and validators
    const currentUser = this.authService.currentUser();
    const user = this.userProfile || currentUser || {};

    this.accountForm.patchValue({
      name: user.first_name || '',
      location: user.timeZone || '',
      email: user.email || user.user_email || '',
      address: user.adresse || '',
      storeName: user.title || '',
      phone: user.phone || ''
    });
    this.accountForm.get('name')?.setValidators(this.isSuperAdmin ? Validators.required : null);
    this.accountForm.get('location')?.setValidators(this.isSuperAdmin ? Validators.required : null);
    this.accountForm.get('email')?.setValidators(this.isSuperAdmin ? [Validators.required, Validators.email] : null);
    this.accountForm.get('address')?.setValidators(this.isSuperAdmin ? Validators.required : null);
    this.accountForm.get('storeName')?.setValidators(this.isSuperAdmin ? Validators.required : null);
    this.accountForm.get('phone')?.setValidators(Validators.required);
    this.accountForm.updateValueAndValidity({ emitEvent: false });

    const firstName = this.userProfile?.first_name || currentUser?.first_name || '';
    const lastName = this.userProfile?.last_name || currentUser?.last_name || '';
    const title = this.userProfile?.title || currentUser?.title || '';
    const email = this.userProfile?.email || this.userProfile?.user_email || currentUser?.email || currentUser?.user_email || '';
    const phone = this.userProfile?.phone || currentUser?.phone || '';
    const userBu = this.userProfile?.user_bu || currentUser?.user_bu || '';
    const location = this.userProfile?.timeZone || currentUser?.timeZone || '';
    const adresse = this.userProfile?.adresse || '';

    this.personalDetailsForm.patchValue({
      firstName,
      lastName,
      title,
      email,
      phone,
      userBu,
      location,
      adresse
    });
    this.personalDetailsForm.get('firstName')?.setValidators(this.isSuperAdmin ? Validators.required : null);
    this.personalDetailsForm.get('lastName')?.setValidators(this.isSuperAdmin ? Validators.required : null);
    this.personalDetailsForm.get('title')?.setValidators(this.isSuperAdmin ? Validators.required : null);
    this.personalDetailsForm.get('email')?.setValidators(this.isSuperAdmin ? [Validators.required, Validators.email] : null);
    this.personalDetailsForm.get('phone')?.setValidators(Validators.required);
    this.personalDetailsForm.get('userBu')?.setValidators(this.isSuperAdmin ? Validators.required : null);
    this.personalDetailsForm.get('location')?.setValidators(this.isSuperAdmin ? Validators.required : null);
    this.personalDetailsForm.get('adresse')?.setValidators(null);
    this.personalDetailsForm.updateValueAndValidity({ emitEvent: false });

    const userEmail = user.email || user.user_email || '';
    this.notificationsForm.patchValue({ email: userEmail });

    const savedItemsPerPage = user.items_per_page || this.defaultItemsPerPage;
    this.configForm.patchValue({ itemsPerPage: savedItemsPerPage });
  }


  passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword');
    const confirmPassword = form.get('confirmPassword');

    if (newPassword && confirmPassword) {
      // Si les deux champs sont remplis et différents
      if (newPassword.value && confirmPassword.value && newPassword.value !== confirmPassword.value) {
        confirmPassword.setErrors({ passwordMismatch: true });
        return { passwordMismatch: true };
      }
      // Si les deux champs correspondent, supprimer l'erreur
      if (newPassword.value && confirmPassword.value && newPassword.value === confirmPassword.value) {
        confirmPassword.setErrors(null);
      }
    }
    return null;
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
  }

  uploadProfileImage(files: File[]): void {
    const file = files[0];
    if (file) {
      // Check the file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        this.helperService.showAlert('Error', 'File type not allowed. Accepted formats: JPG, GIF or PNG.', 'error');
        return;
      }

      // Check the size (800K max)
      if (file.size > 800 * 1024) {
        this.helperService.showAlert('Error', 'The file exceeds the 800K limit.', 'error');
        return;
      }

      const formData = new FormData();
      formData.append('image', file);

      this.dealservice.upload_user_profile_image(formData).subscribe({
        next: (response: any) => {
          // La réponse peut être directement l'objet JSON ou dans response.body selon le type de requête
          let responseData = response;
          if (response && typeof response === 'object' && response.body) {
            responseData = response.body;
          }

          if (responseData && responseData.status === 'success') {
            // Récupérer l'URL de l'image
            const imageUrl = responseData.image_url;

            if (imageUrl) {
              // Mettre à jour le profil utilisateur local
              if (this.userProfile) {
                this.userProfile.image = imageUrl;
              }

              // Mettre à jour l'image avec cache-busting pour forcer le rechargement
              const timestamp = Date.now();
              const separator = imageUrl.includes('?') ? '&' : '?';
              const imageUrlWithCacheBust = `${imageUrl}${separator}t=${timestamp}`;

              // Mettre à jour immédiatement
              this.profileImage = imageUrlWithCacheBust;
              // Forcer la détection de changement Angular
              this.cdr.detectChanges();

              // Vérifier que l'image est accessible et se charge correctement
              const testImg = new Image();
              testImg.onload = () => {
                // Une fois vérifiée, utiliser l'URL sans cache-busting pour l'affichage permanent
                this.profileImage = imageUrl;
                this.cdr.detectChanges();
              };
              testImg.onerror = (err) => {
                // Garder l'URL avec cache-busting en cas d'erreur
              };
              testImg.src = imageUrl;

              // Mettre à jour AuthService avec la nouvelle image
              this.authService.updateUserData({ image: imageUrl });

              // Rafraîchir l'image dans AuthService pour que le header se mette à jour
              this.refreshAuthServiceProfilePic();

              this.helperService.showAlert('Succès!', 'Image de profil mise à jour avec succès.', 'success');
            } else {
              this.helperService.showAlert('Erreur', 'L\'URL de l\'image n\'a pas été retournée par le serveur.', 'error');
            }
          } else {
            this.helperService.showAlert('Erreur', responseData?.message || 'Échec du téléchargement de l\'image', 'error');
          }
        },
        error: (error: any) => {
          this.helperService.showAlert('Erreur', error.error?.message || error.message || 'Échec du téléchargement de l\'image', 'error');
        }
      });
    }
  }

  onImageLoad(event: any): void { }

  onImageError(event: any): void {

    // Si l'image ne peut pas être chargée, essayer différentes solutions
    if (this.profileImage && this.profileImage !== 'assets/images/profile/user-1.jpg' && !this.profileImage.includes('blob:')) {
      // Si l'URL contient déjà le domaine complet, essayer avec cache-busting
      if (this.profileImage.startsWith('https://hr-portal.arturpro.com')) {
        const imageUrlWithCache = this.profileImage + (this.profileImage.includes('?') ? '&' : '?') + 't=' + Date.now();
        this.profileImage = imageUrlWithCache;
        this.cdr.detectChanges();
        return;
      }

      // Si pas d'image dans la base, essayer Microsoft
      if (!this.userProfile?.image) {
        this.loadMicrosoftPhoto();
        return;
      }
    }

    // Sinon, utiliser l'image par défaut
    this.profileImage = 'assets/images/profile/user-1.jpg';
    this.cdr.detectChanges();
  }

  resetProfileImage(): void {
    // Réinitialiser à la photo Microsoft ou image par défaut
    if (this.userProfile && !this.userProfile.image) {
      this.loadMicrosoftPhoto();
    } else {
      this.profileImage = 'assets/images/profile/user-1.jpg';
    }
  }

  toggleNotification(key: string): void {

    const notification = this.notificationConfig.find(n => n.key === key);

    if (!notification) {
      console.error('Notification not found in config:', key);
      return;
    }

    if (!this.canModifyNotification(notification.requiredRoles)) {
      console.warn('User does not have required roles to modify this notification');
      this.helperService.showAlert('Accès refusé', 'Vous n\'avez pas les rôles nécessaires pour modifier cette notification.', 'warning');
      return;
    }

    // Initialiser la clé si elle n'existe pas encore
    if ((this.notificationPreferences as any)[key] === undefined) {
      (this.notificationPreferences as any)[key] = false;
    }

    const oldValue = (this.notificationPreferences as any)[key];
    // Inverser la valeur
    (this.notificationPreferences as any)[key] = !oldValue;

    // Sauvegarder automatiquement après le changement
    this.saveNotifications();
  }

  getNotificationPreference(key: string): boolean {
    // Vérifier si la préférence existe
    const value = (this.notificationPreferences as any)[key];

    // Si la valeur est undefined, initialiser à false par défaut
    if (value === undefined) {
      (this.notificationPreferences as any)[key] = false;
      return false;
    }

    // Retourner true seulement si la valeur est explicitement true
    return value === true || value === 1;
  }

  /**
   * Retourne toutes les notifications disponibles
   */
  getAllNotifications() {
    // Si des rôles sont sélectionnés pour le filtre, filtrer les notifications
    if (this.selectedFilterRoles.length > 0) {
      return this.notificationConfig.filter(notif => {
        // Si la notification n'a pas de rôles requis, elle est accessible à tous
        if (!notif.requiredRoles || notif.requiredRoles.length === 0) {
          return true;
        }
        // Vérifier si au moins un des rôles requis correspond aux rôles sélectionnés
        return notif.requiredRoles.some(role => this.selectedFilterRoles.includes(role));
      });
    }
    return this.notificationConfig;
  }

  /**
   * Charge tous les rôles disponibles depuis le backend
   */
  loadAllRoles(): void {
    this.dealservice.get_groups().subscribe({
      next: (response: any) => {
        if (Array.isArray(response)) {
          this.allAvailableRoles = response.map((role: any) => ({
            id: role.id,
            name: role.name,
            description: role.description,
            selected: false
          }));
          // Créer une copie séparée pour le filtre de pagination
          this.allAvailablePaginationRoles = response.map((role: any) => ({
            id: role.id,
            name: role.name,
            description: role.description,
            selected: false
          }));
        }
      },
      error: (error: any) => {
        console.error('Erreur lors du chargement des rôles:', error);
      }
    });
  }

  /**
   * Toggle la sélection d'un rôle dans le filtre
   */
  toggleRoleFilter(role: any): void {
    role.selected = !role.selected;
    this.updateSelectedFilterRoles();
  }

  /**
   * Met à jour la liste des rôles sélectionnés pour le filtre
   */
  updateSelectedFilterRoles(): void {
    this.selectedFilterRoles = this.allAvailableRoles
      .filter(role => role.selected)
      .map(role => role.name);
  }

  /**
   * Sélectionne tous les rôles
   */
  selectAllRoles(): void {
    this.allAvailableRoles.forEach(role => role.selected = true);
    this.updateSelectedFilterRoles();
  }

  /**
   * Désélectionne tous les rôles
   */
  deselectAllRoles(): void {
    this.allAvailableRoles.forEach(role => role.selected = false);
    this.updateSelectedFilterRoles();
  }

  /**
   * Vérifie si tous les rôles sont sélectionnés
   */
  get allRolesSelected(): boolean {
    return this.allAvailableRoles.length > 0 && this.allAvailableRoles.every(r => r.selected);
  }

  /**
   * Initialise les préférences par défaut pour toutes les notifications
   */
  initializeDefaultPreferences(): void {
    // Pour chaque notification dans la config, s'assurer qu'elle a une préférence
    this.notificationConfig.forEach(notif => {
      if ((this.notificationPreferences as any)[notif.key] === undefined) {
        (this.notificationPreferences as any)[notif.key] = false;
      }
    });
  }

  /**
   * Retourne les notifications accessibles à tous (sans rôles requis)
   */
  getNotificationsForAll() {
    return this.notificationConfig.filter(notif =>
      !notif.requiredRoles || notif.requiredRoles.length === 0
    );
  }

  /**
   * Retourne les notifications avec des rôles requis spécifiques
   */
  getNotificationsWithRoles() {
    return this.notificationConfig.filter(notif =>
      notif.requiredRoles && notif.requiredRoles.length > 0
    );
  }

  /**
   * Vérifie si une notification peut être modifiée par l'utilisateur
   */
  canModifyNotification(requiredRoles: string[]): boolean {
    if (!requiredRoles || requiredRoles.length === 0) {
      return true; // Accessible à tous
    }

    // Vérifier si l'utilisateur a au moins un des rôles requis
    return requiredRoles.some(role => this.userRoles.includes(role));
  }

  selectLocation(location: string): void {
    if (this.isSuperAdmin) {
      this.personalDetailsForm.patchValue({ location });
      this.timezone = location;
    }
  }

  saveAccount(): void {
    if (this.personalDetailsForm.valid) {
      const formValue = this.personalDetailsForm.value;
      const updateData: any = {
        phone: formValue.phone
      };

      // Seul superadmin peut modifier les autres champs
      if (this.isSuperAdmin) {
        updateData.first_name = formValue.firstName;
        updateData.last_name = formValue.lastName;
        updateData.email = formValue.email;
        updateData.title = formValue.title;
        updateData.user_bu = formValue.userBu;
        updateData.timeZone = formValue.location;
      }

      // L'adresse peut être modifiée par tous les utilisateurs
      if (formValue.adresse !== undefined) {
        updateData.adresse = formValue.adresse;
      }

      this.dealservice.update_user_profile(updateData).subscribe({
        next: (response: any) => {
          if (response.status === 'success') {
            // Mettre à jour immédiatement les données dans AuthService avec les nouvelles valeurs
            const currentUser: any = this.authService.currentUser();
            if (currentUser) {
              const updatedUserData: any = {};

              // Mettre à jour les champs modifiés
              if (this.isSuperAdmin) {
                updatedUserData.first_name = formValue.firstName;
                updatedUserData.last_name = formValue.lastName;
                updatedUserData.email = formValue.email;
                updatedUserData.title = formValue.title;
                updatedUserData.user_bu = formValue.userBu;
                updatedUserData.timeZone = formValue.location;
              }
              updatedUserData.phone = formValue.phone;
              if (formValue.adresse !== undefined) {
                updatedUserData.adresse = formValue.adresse;
              }

              // Mettre à jour AuthService immédiatement - pas besoin de recharger
              this.authService.updateUserData(updatedUserData);

              // Mettre à jour aussi le profil local pour l'affichage dans le formulaire
              if (this.userProfile) {
                if (this.isSuperAdmin) {
                  this.userProfile.first_name = formValue.firstName;
                  this.userProfile.last_name = formValue.lastName;
                  this.userProfile.email = formValue.email;
                  this.userProfile.title = formValue.title;
                  this.userProfile.user_bu = formValue.userBu;
                  this.userProfile.timeZone = formValue.location;
                }
                this.userProfile.phone = formValue.phone;
                if (formValue.adresse !== undefined) {
                  this.userProfile.adresse = formValue.adresse;
                }
              }
            }

            this.helperService.showAlert('Succès!', 'Profil mis à jour avec succès.', 'success');
            // Pas besoin de recharger depuis le serveur, les données sont déjà mises à jour
          } else {
            this.helperService.showAlert('Erreur', response.message || 'Échec de la mise à jour du profil', 'error');
          }
        },
        error: (error: any) => {
          console.error('Error updating profile:', error);
          this.helperService.showAlert('Erreur', error.error?.message || 'Échec de la mise à jour du profil', 'error');
        }
      });
    }
  }

  savePassword(): void {
    // Vérifier que le formulaire est valide
    if (!this.passwordForm.valid) {
      // Afficher les erreurs
      if (this.passwordForm.hasError('passwordMismatch')) {
        this.helperService.showAlert('Erreur', 'Les nouveaux mots de passe ne correspondent pas.', 'error');
      } else {
        this.helperService.showAlert('Erreur', 'Veuillez remplir tous les champs correctement.', 'error');
      }
      return;
    }

    const formValue = this.passwordForm.value;

    // Vérifier que les deux nouveaux mots de passe correspondent
    if (formValue.newPassword !== formValue.confirmPassword) {
      this.passwordForm.setErrors({ passwordMismatch: true });
      this.helperService.showAlert('Erreur', 'Les nouveaux mots de passe ne correspondent pas.', 'error');
      return;
    }

    // Vérifier que le nouveau mot de passe est différent de l'ancien
    if (formValue.currentPassword === formValue.newPassword) {
      this.helperService.showAlert('Erreur', 'Le nouveau mot de passe doit être différent de l\'ancien.', 'error');
      return;
    }

    const updateData = {
      currentPassword: formValue.currentPassword,
      password: formValue.newPassword
    };

    this.dealservice.update_user_profile(updateData).subscribe({
      next: (response: any) => {
        if (response.status === 'success') {
          this.helperService.showAlert('Succès!', 'Mot de passe modifié avec succès.', 'success');
          this.passwordForm.reset();
        } else {
          this.helperService.showAlert('Erreur', response.message || 'Échec de la modification du mot de passe', 'error');
        }
      },
      error: (error: any) => {
        console.error('Error updating password:', error);
        this.helperService.showAlert('Erreur', error.error?.message || 'Échec de la modification du mot de passe', 'error');
      }
    });
  }

  saveNotifications(): void {
    // Ne pas afficher d'erreur si le formulaire n'est pas valide, juste logger
    if (!this.notificationsForm.valid) {
      console.warn('Form is not valid, but saving preferences anyway (email may be optional)');
    }

    // Préparer toutes les préférences (true pour activées, false pour désactivées)
    // On envoie toutes les préférences, pas seulement celles qui sont true
    const preferencesToSave: { [key: string]: boolean } = {};

    // Pour chaque notification dans la config, sauvegarder sa valeur actuelle
    this.notificationConfig.forEach(notif => {
      const key = notif.key;
      const value = (this.notificationPreferences as any)[key];
      // Si la valeur est undefined, on la met à false par défaut
      preferencesToSave[key] = value === true;
    });

    // Ajouter aussi les préférences qui ne sont pas dans la config (au cas où)
    Object.keys(this.notificationPreferences).forEach(key => {
      if (!preferencesToSave.hasOwnProperty(key)) {
        preferencesToSave[key] = (this.notificationPreferences as any)[key] === true;
      }
    });

    const updateData = {
      email_preferences: preferencesToSave
    };

    this.dealservice.update_user_profile(updateData).subscribe({
      next: (response: any) => {
        if (response.status === 'success') {
          // Recharger les préférences depuis le backend pour s'assurer qu'elles sont à jour
          this.loadEmailPreferences();
        } else {
          this.helperService.showAlert('Erreur', response.message || 'Échec de l\'enregistrement des préférences', 'error');
        }
      },
      error: (error: any) => {
        this.helperService.showAlert('Erreur', error.error?.message || 'Échec de l\'enregistrement des préférences', 'error');
      }
    });
  }

  saveBilling(): void {
    if (this.billingForm.valid) {
      this.helperService.showAlert('Succès!', 'Informations de facturation enregistrées.', 'success');
    }
  }

  saveConfig(): void {

    // S'assurer que toutes les pages ont une valeur (au moins la valeur par défaut)
    const preferencesToSave: { [key: string]: number } = {};
    this.paginationPages.forEach(page => {
      preferencesToSave[page.pageId] = this.pagePaginationPreferences[page.pageId] || 10;
    });

    // Sauvegarder toutes les préférences UNIQUEMENT en base de données (pas de localStorage)
    this.authService.savePageSizesToBackend(preferencesToSave).subscribe({
      next: (response: any) => {
        if (response.status === 'success') {
          // Recharger le profil depuis la base pour afficher les vraies données sauvegardées
          this.dealservice.get_current_user_profile().subscribe({
            next: (profileResponse: any) => {
              if (profileResponse.status === 'success' && profileResponse.data) {
                this.userProfile = profileResponse.data;
                // Mettre à jour AuthService avec les nouvelles préférences
                if (this.userProfile.pagination_preferences) {
                  this.authService.updateUserData({ pagination_preferences: this.userProfile.pagination_preferences });
                }
                // Recharger les préférences depuis la base de données
                this.loadPaginationPreferences();
                this.helperService.showAlert('Succès!', 'Configuration de pagination sauvegardée avec succès en base de données pour toutes les pages.', 'success');
              }
            },
            error: (err: any) => {
              console.error('Erreur lors du rechargement du profil:', err);
              // Mettre à jour quand même l'affichage
              this.pagePaginationPreferences = { ...preferencesToSave };
              this.cdr.detectChanges();
              this.helperService.showAlert('Succès!', 'Configuration sauvegardée (rechargement du profil échoué).', 'success');
            }
          });
        } else {
          this.helperService.showAlert('Erreur', response.message || 'Échec de la sauvegarde de la configuration', 'error');
        }
      },
      error: (error: any) => {
        this.helperService.showAlert('Erreur', error.error?.message || 'Échec de la sauvegarde de la configuration', 'error');
      }
    });
  }

  enableTwoFactor(): void {
  }

  setupAuthApp(): void {
  }

  setupEmail(): void {
  }

  setupSMS(): void {
  }

  signOutAllDevices(): void {
  }

  changePlan(): void {
  }

  resetPlan(): void {
  }

  cancelSubscription(): void {

  }

  cancel(): void {
    this.loadUserProfile();
  }

  /**
   * Rafraîchit les données utilisateur dans AuthService pour mettre à jour le header
   */
  refreshAuthServiceUser(): void {
    // Recharger les données utilisateur depuis le serveur
    this.dealservice.get_current_user_profile().subscribe({
      next: (response: any) => {
        if (response.status === 'success' && response.data) {
          const currentUser: any = this.authService.currentUser();
          if (currentUser) {
            // Mettre à jour les données dans AuthService
            const updateData: any = {
              first_name: response.data.first_name || currentUser.first_name,
              last_name: response.data.last_name || currentUser.last_name,
              email: response.data.email || currentUser.email,
              title: response.data.title || currentUser.title
            };

            // Ajouter les champs optionnels s'ils existent
            if (response.data.phone) updateData.phone = response.data.phone;
            if (response.data.user_bu) updateData.user_bu = response.data.user_bu;
            if (response.data.timeZone) updateData.timeZone = response.data.timeZone;
            if (response.data.image) updateData.image = response.data.image;

            this.authService.updateUserData(updateData);
          }
        }
      },
      error: (error: any) => {
        console.error('Error refreshing user data:', error);
        // En cas d'erreur, utiliser checkSession comme fallback
        this.authService.checkSession().subscribe();
      }
    });
  }

  /**
   * Rafraîchit l'image de profil dans AuthService
   */
  refreshAuthServiceProfilePic(): void {
    // Si l'image est stockée dans la base, mettre à jour AuthService avec cette URL
    if (this.userProfile && this.userProfile.image) {
      // Créer une URL complète pour l'image
      const imageUrl = this.userProfile.image.startsWith('http')
        ? this.userProfile.image
        : `https://hr-portal.arturpro.com/api/assets/UserImages/${this.userProfile.image}`;

      // Mettre à jour l'image dans AuthService
      this.authService.updateUserData({ image: imageUrl });
      this.authService.setProfilePicFromUrl(imageUrl);
    } else {
      // Sinon, recharger depuis Microsoft Graph
      this.authService.refreshProfilePic();
    }
  }

  /**
   * Navigue vers la page de test des notifications
   */
  goToNotificationTest(): void {
    // Navigation directe vers /notification-test (sans /admin pour éviter le guard)
    this.router.navigate(['/notification-test']).then(
      (success) => {
        if (success) {
        } else {
          this.helperService.showAlert('Erreur de navigation', 'Impossible d\'accéder à la page de test des notifications', 'error');
        }
      },
      (error) => {
        this.helperService.showAlert('Erreur de navigation', 'Erreur lors de l\'accès à la page de test des notifications: ' + (error?.message || 'Erreur inconnue'), 'error');
      }
    );
  }
}
